import { Link, useParams } from 'react-router-dom'
import { useGmud } from '../../../api/queries/gmuds'
import { Abas } from '../../../components/Abas'
import { AvisoDeErro, Carregando, EstadoVazio } from '../../../components/Estados'
import { IconeSeta } from '../../../components/Icones'
import { Cartao } from '../../../components/ListaDeDados'
import { abasDoDetalhe, useAbaNaUrl } from './abas'
import { AbaDoPlano } from './AbaDoPlano'
import { SEM_ACOES_NO_HISTORICO } from './AvisoDeHistoricoMigrado'
import { BotoesTransicao } from './BotoesTransicao'
import { CabecalhoDaGmud } from './CabecalhoDaGmud'
import { Anexos } from './Anexos'
import { LinhaDoTempo } from './LinhaDoTempo'
import { NotasDaGmud } from './NotasDaGmud'
import { PainelDeApoioDoComite } from './PainelDeApoioDoComite'
import { UltimaDecisao } from './UltimaDecisao'
import { PainelDeClassificacao } from './PainelDeClassificacao'
import { PainelDeRisco } from './PainelDeRisco'
import { EtapasDaGmud } from './EtapasDaGmud'
import { SecoesFuturas } from './SecoesFuturas'
import { TEXTOS } from '../../../lib/glossario'
import { enderecoDaLista } from '../../../lib/ultimaBusca'

/**
 * A tela de uma solicitação — montada para decidir, não para folhear.
 *
 * As ações ficam no topo, logo abaixo do estado, e não no fim da página: quem entra aqui
 * vindo do e-mail do comitê vem para decidir, e obrigar a rolar o plano inteiro antes de
 * achar o botão é como se perde a decisão de vista.
 *
 * Identificação e ações ficam FORA das abas, e as abas guardam o que se lê para decidir. Assim
 * trocar de aba nunca esconde de que registro se está falando nem o que dá para fazer com ele —
 * e a aba aberta vai na URL, para o link levar a pessoa ao mesmo lugar.
 *
 * @returns o detalhe da solicitação
 */
export function PaginaDetalheGmud() {
  const { id } = useParams()
  const gmudId = Number(id)
  const { data: gmud, isPending, isError, error } = useGmud(gmudId)
  const { aba, abrir } = useAbaNaUrl()

  if (!Number.isFinite(gmudId)) {
    return (
      <EstadoVazio
        titulo={TEXTOS.enderecoInvalido}
        ajuda="Este link não aponta para uma solicitação. Volte para a lista e abra o registro pelo código."
        acao={<VoltarParaALista />}
      />
    )
  }
  if (isPending) {
    return <Carregando />
  }
  if (isError) {
    return (
      <div className="space-y-4">
        <AvisoDeErro erro={error} titulo="Não foi possível abrir a solicitação" />
        <VoltarParaALista />
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <VoltarParaALista />

      <CabecalhoDaGmud gmud={gmud} />

      {/* A decisão do comitê antes das ações, e de largura inteira: quem responde a ela precisa
          lê-la antes de clicar, em qualquer largura de tela. */}
      {gmud.origem !== 'MIGRADO_V1' && <UltimaDecisao gmudId={gmudId} status={gmud.status} />}

      {/* Duas colunas em tela larga: à direita o que se FAZ (ações e de quem se espera), à
          esquerda o que se LÊ para decidir. Antes era uma coluna só, e as sete etapas empurravam o
          plano para o segundo terço da página. A coluna das ações vem primeiro no DOM — é também a
          ordem em tela estreita e a do leitor de tela: quem chega pelo e-mail do comitê vem decidir. */}
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_22rem] xl:items-start">
        <aside aria-label="O que fazer com esta solicitação" className="space-y-5 xl:order-2">
          <Cartao
            titulo="O que você pode fazer agora"
            descricao="As ações são as que o servidor libera para o seu perfil neste estado da solicitação."
          >
            <BotoesTransicao
              gmudId={gmudId}
              semAcoes={gmud.origem === 'MIGRADO_V1' ? SEM_ACOES_NO_HISTORICO : undefined}
            />
          </Cartao>

          {/* A pergunta que a etiqueta de estado não responde é 'de quem estamos esperando', e ela
              mora ao lado das ações: é a mesma pergunta vista do outro lado. */}
          <EtapasDaGmud status={gmud.status} />
        </aside>

        <div className="min-w-0 space-y-5 xl:order-1">
          {/* Antes das abas: é para a decisão que o apoio existe. A recomendação da IA e a revisão
              do Copilot são insumo — o clique de aprovar continua humano. */}
          <PainelDeApoioDoComite gmudId={gmudId} origem={gmud.origem} status={gmud.status} />

          <Abas
            abas={abasDoDetalhe(gmud.pendencias.length)}
            ativa={aba}
            aoMudar={abrir}
            rotulo="Seções da solicitação"
          >
            {aba === 'plano' && <AbaDoPlano gmud={gmud} />}
            {aba === 'anexos' && <Anexos gmudId={gmudId} />}
            {aba === 'classificacao' && (
              <div className="space-y-5">
                <PainelDeClassificacao gmud={gmud} />
                <PainelDeRisco gmud={gmud} />
              </div>
            )}
            {aba === 'historico' && <LinhaDoTempo gmudId={gmudId} origem={gmud.origem} />}
          </Abas>

          {gmud.origem !== 'MIGRADO_V1' && <NotasDaGmud gmudId={gmudId} />}

          <SecoesFuturas />
        </div>
      </div>
    </div>
  )
}

/** A saída da tela. Mantém a mesma frase nos três estados da página — inclusive no de erro. */
function VoltarParaALista() {
  return (
    <Link
      to={enderecoDaLista('/gmuds')}
      className="inline-flex items-center gap-1.5 text-sm text-niteroi-700 underline underline-offset-2 hover:text-niteroi-900"
    >
      <IconeSeta className="h-4 w-4 rotate-180" />
      {TEXTOS.voltarParaALista}
    </Link>
  )
}
